/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Input, Button, Tag, Segmented, Modal, Space, Spin, InputNumber, Select, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { formatCurrency, orderProductSubtotal } from "../../../utils/formatters";
import { formatOrderNo } from "../../../shared/lib/documentNumbers";
import { parseLocalizedName } from "../../../utils/localization";
import { apiClient } from "../../../shared/api/AllApi";
import { getApiErrorMessage } from "../../../shared/api/errors";
import { createRequestGuard, isAbortError } from "../../../shared/lib/request";
import usePermissions from "../../../hooks/usePermissions";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";
import { OrderedListOutlined, DollarOutlined, PrinterOutlined, EyeOutlined, RollbackOutlined, EditOutlined, StopOutlined } from "@ant-design/icons";

const TableOrder = () => {
  const { hasPermission } = usePermissions();
  const canEditOrder = hasPermission("pos.edit") || hasPermission("orders.edit");
  const canCancelSale = hasPermission("pos.cancel");
  const [SearchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [dataSource, setDataSource] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const listGuardRef = useRef(createRequestGuard());
  const { pagination, resetPage } = useTablePagination();

  // ── View / return modal ──────────────────────────────────────────
  const [viewOpen, setViewOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [returnMode, setReturnMode] = useState(false);
  const [returnQtys, setReturnQtys] = useState({});
  const [returnWeights, setReturnWeights] = useState({});
  const [returnReasons, setReturnReasons] = useState({});
  const [returning, setReturning] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelRefundMethod, setCancelRefundMethod] = useState("cash");
  const [cancelling, setCancelling] = useState(false);

  const openView = async (record) => {
    setViewOpen(true); setViewOrder(null); setReturnMode(false); setViewLoading(true);
    try {
      // Always use /orders/:id (orders.view). POS return amounts are included for source=pos.
      // Do not call /pos/sales here — that requires pos.sell and toasts 403 for order-desk users.
      const r = await apiClient.get(`/orders/${record.id}`);
      setViewOrder({ ...r.data, source: r.data.source || record.source });
    } catch {
      message.error(t("noDataFound", "No data found"));
    } finally {
      setViewLoading(false);
    }
  };

  const viewItems = viewOrder ? (viewOrder.order_products || viewOrder.items || []) : [];
  const isPos = viewOrder?.source === "pos";
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const soldQtyOf = (it) => num(it.sold_qty ?? it.quantity);
  const soldWeightOf = (it) => num(it.sold_weight ?? it.weight);
  const availQty = (it) => (it.available_qty != null && it.available_qty !== "" ? Math.max(0, num(it.available_qty)) : soldQtyOf(it));
  const availWeight = (it) => (it.available_weight != null && it.available_weight !== "" ? Math.max(0, num(it.available_weight)) : soldWeightOf(it));
  const availAmt = (it) => {
    if (it.available_amount != null && it.available_amount !== "") return Math.max(0, num(it.available_amount));
    const w = soldWeightOf(it);
    return w > 0 ? w : soldQtyOf(it);
  };
  const unitPriceOf = (it) => num(it.single_price ?? it.unit_price);
  const billingReturnOf = (it) => {
    const w = Math.min(num(returnWeights[it.id]), availWeight(it));
    const q = Math.min(num(returnQtys[it.id]), availQty(it));
    return soldWeightOf(it) > 0 ? w : q;
  };
  const lineProductRefund = (it) => {
    const sold = soldWeightOf(it) > 0 ? soldWeightOf(it) : soldQtyOf(it);
    const billing = billingReturnOf(it);
    const lineTotal = num(it.total_price);
    if (sold > 0 && lineTotal > 0) return lineTotal * (billing / sold);
    return billing * unitPriceOf(it);
  };
  const canReturn = isPos
    && viewOrder?.status === "confirmed"
    && hasPermission("pos.return")
    && viewItems.some((it) => availAmt(it) > 0 || availQty(it) > 0 || availWeight(it) > 0);
  const canCancel = isPos
    && viewOrder?.status === "confirmed"
    && canCancelSale;
  const productCashBack = viewItems.reduce((s, it) => s + lineProductRefund(it), 0);
  const linesBase = viewItems.reduce((s, it) => s + num(it.total_price), 0);
  const orderTotal = num(viewOrder?.total_price);
  const storedComm = num(viewOrder?.commission_amount);
  const orderComm = storedComm > 0 ? storedComm : Math.max(0, orderTotal - linesBase);
  const orderBase = Math.max(0.01, linesBase > 0 ? linesBase : orderTotal - orderComm);
  const commissionCashBack = orderComm > 0 ? orderComm * (productCashBack / orderBase) : 0;
  const returnCashBack = productCashBack + commissionCashBack;

  const startReturn = () => {
    const q = {}, w = {}, rs = {};
    viewItems.forEach((it) => {
      q[it.id] = 0;
      w[it.id] = 0;
      rs[it.id] = t("posOrdersDefaultReturnReason", "Customer return");
    });
    setReturnQtys(q); setReturnWeights(w); setReturnReasons(rs); setReturnMode(true);
  };

  const submitReturn = async () => {
    const items = viewItems.map((it) => ({
      order_product_id: it.id,
      product_id: it.product_id,
      return_qty: Math.min(parseFloat(returnQtys[it.id] || 0), availQty(it)),
      return_weight: Math.min(parseFloat(returnWeights[it.id] || 0), availWeight(it)),
      reason: returnReasons[it.id] || null,
    })).filter((x) => x.return_qty > 0 || x.return_weight > 0);
    if (items.length === 0) { message.warning(t("posOrdersReturnNothing", "Enter a quantity or weight to return")); return; }
    setReturning(true);
    try {
      await apiClient.post(`/pos/sales/${viewOrder.id}/return`, { items });
      message.success(t("posOrdersReturnSuccess", "Return recorded"));
      const r = await apiClient.get(`/orders/${viewOrder.id}`);
      setViewOrder({ ...r.data, source: r.data.source || "pos" });
      setReturnMode(false);
      GetApiOrder();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posOrdersReturnFailed", "Return failed")));
    } finally {
      setReturning(false);
    }
  };

  const submitCancel = async () => {
    if (!viewOrder) return;
    if (cancelNote.trim().length < 3) {
      message.warning(t("posOrdersCancelReasonRequired", "Enter a cancellation reason"));
      return;
    }
    setCancelling(true);
    try {
      const response = await apiClient.post(`/pos/sales/${viewOrder.id}/cancel`, {
        note: cancelNote.trim(),
        refund_method: cancelRefundMethod,
      });
      message.success(
        `${t("posOrdersCancelSuccess", "Sale cancelled")} · ${t("refunded", "Refunded")}: ${formatCurrency(response.data?.refund_amount || 0)}`
      );
      setCancelModalOpen(false);
      setCancelNote("");
      setViewOpen(false);
      setViewOrder(null);
      GetApiOrder();
    } catch (e) {
      message.error(getApiErrorMessage(e, t("posOrdersCancelFailed", "Cancel failed")));
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    GetApiOrder();
    return () => listGuardRef.current.abort();
  }, []);

  const GetApiOrder = async () => {
    const req = listGuardRef.current.next();
    setListLoading(true);
    try {
      const res = await apiClient.get("/orders", { signal: req.signal });
      if (!req.isCurrent()) return;
      setDataSource(res.data.map((item) => {
        const totalReturned = parseFloat(item.total_returned || 0);
        const hasReturns = Boolean(item.has_returns) || totalReturned > 0;
        const rawStatus = item.status || "confirmed";
        // Prefer API list_status; otherwise treat any return activity as returned for filters/tags.
        const listStatus = item.list_status
          || (hasReturns && rawStatus === "confirmed" ? "returned" : rawStatus);
        const totalPrice = parseFloat(item.total_price || 0) || 0;
        const commission = parseFloat(item.commission_amount || 0) || 0;
        const isTaxed = !(item.is_taxed === false || item.is_taxed === 0);
        // Real product subtotal (API product_subtotal / lines) — never total − commission
        // (grand total is denomination-rounded and would warp the subtotal).
        const subtotal = orderProductSubtotal(item) || (commission > 0 ? 0 : totalPrice);
        return {
          id: item.id,
          document_no: item.document_no || formatOrderNo(item.id, item.created_at),
          customer_id: item.customer ? item.customer.id : null,
          total_price: totalPrice,
          commission_amount: commission,
          is_taxed: isTaxed,
          product_subtotal: subtotal,
          subtotal,
          amount_paid: item.amount_paid,
          refunded_amount: parseFloat(item.refunded_amount || 0),
          net_paid: Math.max(0, parseFloat(item.amount_paid || 0) - parseFloat(item.refunded_amount || 0)),
          outstanding_balance: parseFloat(item.outstanding_balance || 0),
          total_returned: totalReturned,
          has_returns: hasReturns,
          customer: item.customer ? item.customer.name : t("noCustomer", "No Customer"),
          Type: item.customer ? item.customer.type : t("unknown", "Unknown"),
          Number: item.customer ? item.customer.phone_number : t("noNumber", "No Number"),
          created_at: item.created_at,
          source: item.source || "shop",
          status: listStatus,
          raw_status: rawStatus,
          payment_status: item.payment_status,
        };
      }));
    } catch (e) {
      if (isAbortError(e)) return;
      if (req.isCurrent()) setDataSource([]);
    } finally {
      if (req.isCurrent()) setListLoading(false);
    }
  };

  const filteredData = dataSource.filter((item) => {
    const search = SearchText.toLowerCase();
    const matchesSearch =
      item.customer.toLowerCase().includes(search) ||
      String(item.document_no || "").toLowerCase().includes(search) ||
      String(item.id).includes(search) ||
      String(item.Number || "").toLowerCase().includes(search) ||
      String(item.total_price).includes(search);
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    let matchesDate = true;
    if (dateRange?.[0] && dateRange?.[1] && item.created_at) {
      const created = dayjs(item.created_at);
      matchesDate = !created.isBefore(dateRange[0]) && !created.isAfter(dateRange[1]);
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Stats follow the same search / status / date filters as the table.
  const totalRevenue = filteredData.reduce((acc, i) => acc + parseFloat(i.net_paid || 0), 0);
  const totalDebt = filteredData.reduce((acc, i) => {
    if (i.raw_status === "cancelled" || i.status === "cancelled") return acc;
    return acc + Math.max(0, i.outstanding_balance);
  }, 0);

  const statusMeta = {
    confirmed: { color: "green", label: t("statusConfirmed", "Confirmed") },
    completed: { color: "blue", label: t("statusCompleted", "Completed") },
    cancelled: { color: "red", label: t("statusCancelled", "Cancelled") },
    returned: { color: "orange", label: t("statusReturned", "Returned") },
    pending: { color: "gold", label: t("statusPending", "Pending") },
  };
  const statusTag = (status, record) => {
    const meta = statusMeta[status] || { color: "default", label: status || "—" };
    const partial = status === "returned" && record?.raw_status === "confirmed" && record?.has_returns;
    const label = partial ? (t("statusPartialReturn", "Partial return")) : meta.label;
    return <Tag color={meta.color} style={{ fontWeight: 600, borderRadius: 6 }}>{label}</Tag>;
  };
  const cancelledCount = filteredData.filter((i) => i.status === "cancelled").length;
  const returnedCount = filteredData.filter((i) => i.status === "returned" || i.has_returns).length;

  const columns = [
    {
      title: "#",
      key: "index",
      width: 44,
      render: (_, __, index) => <span style={{ color: T.muted, fontSize: 13 }}>{index + 1}</span>,
    },
    {
      title: <b>{t("orderNo", "Bill #")}</b>,
      dataIndex: "document_no",
      key: "document_no",
      width: 150,
      render: (_, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ fontWeight: 700, color: T.primary, fontFamily: "monospace", fontSize: 12 }}>
          {record.document_no || formatOrderNo(record.id, record.created_at)}
        </Link>
      ),
    },
    {
      title: <b>{t("customer", "Customer")}</b>,
      dataIndex: "customer",
      key: "customer",
      render: (text, record) =>
        record.customer_id ? (
          <Link to={`/customers/${record.customer_id}`} style={{ fontWeight: 600, color: T.text }}>
            {text}
          </Link>
        ) : (
          <span style={{ color: T.sub }}>{text}</span>
        ),
    },
    {
      title: <b>{t("status", "Status")}</b>,
      dataIndex: "status",
      key: "status",
      render: (status, record) => statusTag(status, record),
    },
    {
      title: <b>{t("number", "Number")}</b>,
      dataIndex: "Number",
      key: "Number",
      render: (text, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ color: T.sub }}>{text}</Link>
      ),
    },
    {
      title: <b>{t("amountPaid", "Amount Paid")}</b>,
      dataIndex: "net_paid",
      key: "amount_paid",
      render: (text, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ fontWeight: 600, color: T.green }}>
          {formatCurrency(text)}
        </Link>
      ),
    },
    {
      title: <b>{t("refunded", "Refunded")}</b>,
      dataIndex: "refunded_amount",
      key: "refunded_amount",
      render: (text) => <span style={{ color: T.red }}>{formatCurrency(text)}</span>,
    },
    {
      title: <b>{t("posTerminalSubtotal", "Subtotal")}</b>,
      dataIndex: "subtotal",
      key: "subtotal",
      render: (text, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ fontWeight: 700, color: T.text }}>
          {formatCurrency(text != null ? text : record.total_price)}
        </Link>
      ),
    },
    {
      title: <b>{t("totalPrice", "Total")}</b>,
      dataIndex: "total_price",
      key: "total_price",
      render: (text, record) => (
        <Link to={`/orders/${record.id}/view`} style={{ fontWeight: 700, color: T.sub }}>
          {formatCurrency(text)}
          {record.commission_amount > 0 ? (
            <span style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#d97706" }}>
              +{formatCurrency(record.commission_amount)}
            </span>
          ) : null}
        </Link>
      ),
    },
    {
      title: <b>{t("createdAt", "Created At")}</b>,
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => <span style={{ color: T.muted, fontSize: 13 }}>{text ? new Date(text).toLocaleDateString() : "-"}</span>,
    },
    {
      title: t("actions", "Actions"),
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space>
          {canEditOrder && record.status !== "cancelled" && record.status !== "returned" && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/orders/${record.id}/edit`)}
              style={{ borderRadius: 7 }}
            >
              {t("edit", "Edit")}
            </Button>
          )}
          <Button
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => navigate(`/orders/${record.id}/view`)}
            style={{ borderRadius: 7 }}
          >
            {t("print", "Print")}
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openView(record)}
            style={{ borderRadius: 7 }}
          >
            {t("view", "View")}
          </Button>
        </Space>
      ),
    },
  ];

  const itemColumns = [
    {
      title: <b>{t("product", "Product")}</b>, key: "product",
      render: (_, it) => <b style={{ color: T.text }}>{parseLocalizedName(it.product?.name, i18n.language, `#${it.product_id}`)}</b>,
    },
    {
      title: <b>{t("posPurchaseVendor", "Farmer")}</b>, key: "farmer",
      render: (_, it) => <span style={{ color: T.primary }}>{it.vendor?.name || "—"}</span>,
    },
    {
      title: <b>{t("qty", "Qty")}</b>, key: "qty", align: "center", width: 80,
      render: (_, it) => {
        const q = parseFloat(it.quantity) || 0;
        return q > 0 ? q : <span style={{ color: T.muted }}>—</span>;
      },
    },
    {
      title: <b>{t("weight", "Weight")}</b>, key: "weight", align: "center", width: 100,
      render: (_, it) => {
        const w = parseFloat(it.weight) || 0;
        return w > 0 ? `${w} ${t("kg", "kg")}` : <span style={{ color: T.muted }}>—</span>;
      },
    },
    {
      title: <b>{t("price", "Unit Price")}</b>, dataIndex: "single_price", key: "single_price", align: "center",
      render: (v) => formatCurrency(v),
    },
    {
      title: <b>{t("total", "Total")}</b>, key: "total", align: "right",
      render: (_, it) => <b>{formatCurrency(it.total_price)}</b>,
    },
    ...(returnMode ? [{
      title: <b>{t("posOrdersReturn", "Return")}</b>, key: "ret", align: "center", width: 300,
      render: (_, it) => {
        const aQ = availQty(it);
        const aW = availWeight(it);
        const sQ = soldQtyOf(it);
        const sW = soldWeightOf(it);
        if (aQ <= 0 && aW <= 0 && availAmt(it) <= 0) return <span style={{ color: T.muted }}>—</span>;
        return (
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flexWrap: "wrap", justifyContent: "center" }}>
            <div>
              <div style={{ fontSize: 10, color: "#1d4ed8", fontWeight: 800 }}>{t("posReturnCases", "Cases")}</div>
              <InputNumber
                min={0}
                max={Math.max(aQ, sQ, 0) || undefined}
                step={1}
                controls={false}
                placeholder={t("qty", "qty")}
                value={returnQtys[it.id] ?? 0}
                onChange={(v) => setReturnQtys((p) => ({ ...p, [it.id]: v ?? 0 }))}
                style={{ width: 78, background: "#eff6ff" }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#15803d", fontWeight: 800 }}>{t("kg", "kg")}</div>
              <InputNumber
                min={0}
                max={Math.max(aW, sW, 0) || undefined}
                step={0.5}
                controls={false}
                placeholder={t("kg", "kg")}
                value={returnWeights[it.id] ?? 0}
                onChange={(v) => setReturnWeights((p) => ({ ...p, [it.id]: v ?? 0 }))}
                style={{ width: 78, background: "#f0fdf4" }}
              />
            </div>
            <Input
              placeholder={t("reason", "Reason")}
              value={returnReasons[it.id] ?? ""}
              onChange={(e) => setReturnReasons((p) => ({ ...p, [it.id]: e.target.value }))}
              style={{ width: 100 }}
            />
          </div>
        );
      },
    }] : []),
  ];

  const stats = [
    <StatCard key="total" icon={<OrderedListOutlined style={{ color: "#2563eb" }} />} label={t("orders", "Orders")} value={filteredData.length} iconBg="#dbeafe" />,
    <StatCard key="revenue" icon={<DollarOutlined style={{ color: "#16a34a" }} />} label={t("amountPaid", "Amount Paid")} value={formatCurrency(totalRevenue)} iconBg="#dcfce7" valueColor={T.green} />,
    <StatCard key="debt" icon={<DollarOutlined style={{ color: "#e11d48" }} />} label={t("outstanding", "Outstanding")} value={formatCurrency(totalDebt)} iconBg="#fee2e2" valueColor={T.red} />,
  ];

  const toolbar = (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <Input.Search
        placeholder={t("searchPlaceholder", "Search here...")}
        style={{ width: 300, borderRadius: 9 }}
        onSearch={(v) => { setSearchText(v); resetPage(); }}
        onChange={(e) => { setSearchText(e.target.value); resetPage(); }}
      />
      <DateRangeFilter
        value={dateRange}
        onChange={(v) => { setDateRange(v); resetPage(); }}
        showTime
        allowClear
        size="small"
      />
      <Segmented
        value={statusFilter}
        onChange={(v) => { setStatusFilter(v); resetPage(); }}
        options={[
          { label: `${t("all", "All")} (${dataSource.length})`, value: "all" },
          { label: statusMeta.confirmed.label, value: "confirmed" },
          { label: `${statusMeta.returned.label} (${returnedCount})`, value: "returned" },
          { label: `${statusMeta.cancelled.label} (${cancelledCount})`, value: "cancelled" },
        ]}
      />
    </div>
  );

  return (
    <>
    <ListPage
      breadcrumb={[t("sales", "Sales"), t("orders", "Orders")]}
      title={t("orders", "Orders")}
      subtitle={`${filteredData.length} ${t("orders", "Orders").toLowerCase()}`}
      stats={stats}
      toolbar={toolbar}
    >
      <Table
        dataSource={filteredData.map((item) => ({ ...item, key: item.id }))}
        columns={columns}
        loading={listLoading}
        pagination={pagination}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t("noDataFound", "No data found") }}
        onRow={(record) => ({
          style: (record.status === "cancelled" || record.status === "returned")
            ? { opacity: 0.55 }
            : undefined,
        })}
      />
    </ListPage>

      <Modal
        open={viewOpen}
        onCancel={() => { setViewOpen(false); setReturnMode(false); }}
        footer={null}
        width={920}
        centered
        destroyOnHidden
        styles={{ body: { paddingTop: 12 } }}
        title={
          viewOrder
            ? (
              <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontWeight: 800 }}>
                  {viewOrder.document_no || formatOrderNo(viewOrder.id, viewOrder.created_at)}
                </span>
                {viewOrder.source === "pos" && <Tag color="blue" style={{ margin: 0 }}>{t("sourcePos", "POS")}</Tag>}
                {(viewOrder.status === "returned" || parseFloat(viewOrder.total_returned || 0) > 0) && (
                  <Tag color="orange" style={{ margin: 0 }}>
                    {viewOrder.status === "returned"
                      ? t("statusReturned", "Returned")
                      : t("statusPartialReturn", "Partial return")}
                  </Tag>
                )}
                {viewOrder.status === "cancelled" && (
                  <Tag color="red" style={{ margin: 0 }}>{t("statusCancelled", "Cancelled")}</Tag>
                )}
              </span>
            )
            : t("view", "View")
        }
      >
        {viewLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}><Spin /></div>
        ) : viewOrder && (() => {
          const total = Number(viewOrder.total_price || 0);
          const commission = Number(viewOrder.commission_amount || 0);
          const isTaxed = !(viewOrder.is_taxed === false || viewOrder.is_taxed === 0);
          // Sum real line amounts — do not derive from neared customer total.
          const subtotal = orderProductSubtotal(viewOrder) || linesBase;
          const paid = Number(viewOrder.amount_paid || 0);
          const refunded = Number(viewOrder.refunded_amount || 0);
          const remaining = Number(viewOrder.outstanding_balance != null
            ? viewOrder.outstanding_balance
            : Math.max(0, total - paid));
          const row = (label, value, opts = {}) => (
            <div style={{
              display: "flex", justifyContent: "space-between", gap: 12,
              padding: "8px 0", borderBottom: opts.last ? "none" : `1px solid ${T.border}`,
              fontSize: opts.large ? 15 : 13,
            }}>
              <span style={{ color: opts.muted ? T.muted : T.sub }}>{label}</span>
              <b style={{ color: opts.color || T.text, fontWeight: opts.large ? 800 : 600 }}>{value}</b>
            </div>
          );
          return (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.7fr) minmax(240px, 1fr)", gap: 20, alignItems: "start" }}>
              <div>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "baseline",
                  marginBottom: 10, gap: 8, flexWrap: "wrap",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: T.text }}>
                    {t("saleItemList", "Sale items")}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontVariantNumeric: "tabular-nums" }}>
                    {viewOrder.created_at ? dayjs(viewOrder.created_at).format("DD/MM/YYYY HH:mm") : ""}
                  </div>
                </div>
                <Table
                  size="small"
                  rowKey={(r) => r.id}
                  dataSource={viewItems}
                  columns={itemColumns}
                  pagination={false}
                  locale={{ emptyText: t("noDataFound", "No data found") }}
                  style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}
                />

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontVariantNumeric: "tabular-nums" }}>
                    {viewItems.length} {t("items", "items")}
                    {parseFloat(viewOrder.total_returned) > 0 && (
                      <span style={{ color: T.red, fontWeight: 700, marginInlineStart: 10 }}>
                        {t("posOrdersReturned", "Returned")}: −{formatCurrency(viewOrder.total_returned)}
                      </span>
                    )}
                  </div>
                  <Space wrap>
                    <Button
                      icon={<PrinterOutlined />}
                      style={{ borderRadius: 8 }}
                      onClick={() => navigate(`/orders/${viewOrder.id}/view`)}
                    >
                      {t("print", "Print")}
                    </Button>
                    {canReturn && !returnMode && (
                      <Button danger icon={<RollbackOutlined />} style={{ borderRadius: 8 }} onClick={startReturn}>
                        {t("posOrdersReturn", "Return Items")}
                      </Button>
                    )}
                    {canCancel && !returnMode && (
                      <Button
                        danger
                        type="primary"
                        icon={<StopOutlined />}
                        style={{ borderRadius: 8 }}
                        onClick={() => {
                          setCancelNote("");
                          setCancelRefundMethod(viewOrder.payment_method === "cash" ? "cash" : "credit");
                          setCancelModalOpen(true);
                        }}
                      >
                        {t("posOrdersCancel", "Cancel Sale")}
                      </Button>
                    )}
                  </Space>
                </div>

                {returnMode && (
                  <div className="order-view-return-panel">
                    <div style={{ fontWeight: 700, marginBottom: 6, color: T.text }}>
                      {t("posOrdersReturnTitle", "Return Management")}
                    </div>
                    <div style={{ fontSize: 12, color: T.sub, marginBottom: 10 }}>
                      {t("posOrdersReturnHintBoth", "Enter both Cases (qty) and Weight (kg) so the farmer sees what was returned. Customer refund includes commission.")}
                    </div>
                    <div style={{ textAlign: "center", fontWeight: 800, color: T.green, fontSize: 16, marginBottom: 4 }}>
                      {t("posOrdersCashBack", "Total Cash Back")}: {formatCurrency(returnCashBack)}
                    </div>
                    <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 12, color: T.sub }}>
                      {t("posTerminalSubtotal", "Product")}: {formatCurrency(productCashBack)}
                      {orderComm > 0 && (
                        <span style={{ color: "#d97706" }}>
                          {" + "}{t("posTerminalCommission", "Commission")}: {formatCurrency(commissionCashBack)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <Button style={{ borderRadius: 8 }} onClick={() => setReturnMode(false)}>{t("cancel", "Cancel")}</Button>
                      <Button danger type="primary" style={{ borderRadius: 8 }} loading={returning} disabled={returnCashBack <= 0} onClick={submitReturn}>
                        {t("posOrdersReturnConfirm", "Process Returns")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="order-view-summary-side">
                <div style={{ fontWeight: 700, fontSize: 14, color: T.text, marginBottom: 10 }}>
                  {t("orderSummary", "Sale summary")}
                </div>
                {row(t("customer", "Customer"), viewOrder.customer?.name || t("walkInCustomer", "Walk-in"))}
                {row(
                  t("paymentMethod", "Payment"),
                  viewOrder.payment_method === "credit"
                    ? t("posTerminalCredit", "Credit")
                    : viewOrder.payment_method === "cash"
                      ? t("posTerminalCash", "Cash")
                      : "—"
                )}
                {isTaxed && commission > 0 && (
                  <>
                    {row(t("posTerminalSubtotal", "Subtotal"), formatCurrency(subtotal))}
                    {row(t("posTerminalCommission", "Commission"), formatCurrency(commission), { color: "#d97706" })}
                  </>
                )}
                {row(t("total", "Total"), formatCurrency(total), { large: true })}
                {row(t("amountPaid", "Amount paid"), formatCurrency(paid), { color: T.green })}
                {refunded > 0 && row(t("refunded", "Refunded"), `−${formatCurrency(refunded)}`, { color: T.red })}
                {row(
                  t("outstanding", "Outstanding"),
                  formatCurrency(remaining),
                  { color: remaining > 0.001 ? T.red : T.green, last: true, large: true }
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={cancelModalOpen}
        onCancel={() => setCancelModalOpen(false)}
        title={
          <span>
            <StopOutlined /> {t("posOrdersCancelTitle", "Cancel Sale")}{" "}
            {viewOrder?.document_no || formatOrderNo(viewOrder?.id, viewOrder?.created_at)}
          </span>
        }
        okText={t("posOrdersCancelOk", "Confirm Cancel")}
        okButtonProps={{ danger: true, loading: cancelling, disabled: cancelNote.trim().length < 3 }}
        onOk={submitCancel}
        width={400}
      >
        <p style={{ color: T.sub, fontSize: 13, marginBottom: 12 }}>
          {t("posOrdersCancelWhy", "Why is this sale being cancelled? (required)")}
        </p>
        <Input.TextArea
          rows={3}
          placeholder={t("posOrdersCancelPlaceholder", "Enter cancellation reason...")}
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
        />
        {cancelNote.trim().length < 3 && (
          <div style={{ fontSize: 11, color: T.sub, marginTop: 4 }}>
            {t("posOrdersCancelMinLength", "Enter at least 3 characters to continue")}
          </div>
        )}
        {Number(viewOrder?.amount_paid || 0) - Number(viewOrder?.refunded_amount || 0) > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: T.sub, marginBottom: 5 }}>
              {t("refundMethod", "Refund method")}
            </div>
            <Select
              value={cancelRefundMethod}
              onChange={setCancelRefundMethod}
              style={{ width: "100%" }}
              options={[
                { value: "cash", label: t("cash", "Cash") },
                { value: "credit", label: t("credit", "Credit") },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default TableOrder;
